import React from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import { connect } from 'react-redux';
import * as analytics from '@eb/site-analytics';
import { collections as collectionsDuck } from '@eb/destination-utils';
import { LoadingCards } from '@eb/eds-event-card';

import {
    setHoverEventTarget as setHoverEventTargetAction,
    setGoogleMapsLoaded as setGoogleMapsLoadedAction,
} from '../actions/ui';
import { saveEvent as saveEventAction } from '../actions/event';
import { trackEventClickInternal as trackEventClickInternalAction } from '../actions/analytics';
import { getEventExpansions as getEventExpansionsAction } from '../actions/expansions';

import { getSearchContent, getSearchSection } from '../utils';
import { hasEventResults } from '../utils/events';
import { addSearchIdToGaAction } from '../utils/analytics';

import OnlineEventsBannerContainer from './OnlineEventsBannerContainer';
import SearchEventCardStyleSwitcher from '../components/SearchEventCardStyleSwitcher';
import {
    GA_SEARCH_CATEGORY,
    SHARE_OPTIONS_PROPTYPE,
    SEARCH_SECTION,
    SEARCH_EVENT_NAVIGATION,
} from '../constants';

const { trackEventFromState, trackEventFromStateOnPageUnload } = analytics;

const {
    actions: {
        addOrRemoveEventToCollection: addOrRemoveEventToCollectionAction,
        createCollection: createCollectionAction,
    },
    selectors: { getFormattedCollections },
} = collectionsDuck;

import './SearchResultsPanelContainer.scss';

const LIST_TYPE = 'list';

const SearchHeaderContainer = loadable(() => import('./SearchHeaderContainer'));

const ResultGroup = ({
    content,
    shareOptions,
    onSaveEvent,
    isAuthenticated,
    isMobile,
    trackClick,
    trackClickInternal,
    trackGAEvent,
    onMouseOverCard,
    onMouseOutCard,
    featureFlags,
    userCollections,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
    shouldShowDebugOutput,
    history,
}) => (
    <ul className="search-main-content__events-list">
        {content.map(({ id, ...cardProps }, index) => (
            <li key={id}>
                <div
                    className="search-event-card-wrapper"
                    onMouseEnter={onMouseOverCard.bind(null, id)}
                    onFocus={onMouseOverCard.bind(null, id)}
                    onMouseLeave={onMouseOutCard}
                    onBlur={onMouseOutCard}
                    role="presentation"
                >
                    <SearchEventCardStyleSwitcher
                        id={id}
                        trackClick={trackClick.bind(null, index, id)}
                        type={LIST_TYPE}
                        shareOptions={shareOptions}
                        isAuthenticated={isAuthenticated}
                        onSaveEvent={onSaveEvent}
                        containerSpacingClasses={
                            isMobile ? '' : 'eds-l-pad-all-4'
                        }
                        trackClickInternal={trackClickInternal.bind(
                            null,
                            index,
                        )}
                        trackGAEvent={trackGAEvent}
                        section={SEARCH_SECTION}
                        featureFlags={featureFlags}
                        userCollections={userCollections}
                        onAddOrRemoveEventToCollection={
                            onAddOrRemoveEventToCollection
                        }
                        onCreateCollection={onCreateCollection}
                        position={index + 1}
                        shouldShowDebugOutput={shouldShowDebugOutput}
                        history={history}
                        {...cardProps}
                    />
                </div>
            </li>
        ))}
    </ul>
);

// Exporting for use in exp_eb_92146_integrated_content_on_search
export const SearchResults = ({
    results,
    shareOptions,
    onSaveEvent,
    isAuthenticated,
    isLoading,
    isMobile,
    trackClick,
    trackClickInternal,
    trackGAEvent,
    onMouseOverCard,
    onMouseOutCard,
    featureFlags,
    userCollections,
    onAddOrRemoveEventToCollection,
    onCreateCollection,
    shouldShowDebugOutput,
    history,
}) => {
    let component = <LoadingCards style={isMobile ? 'list-square' : 'list'} />;

    if (!isLoading) {
        component = (
            <div className="search-main-content">
                {results.map(({ content, type }) => {
                    const key = `search_${type}`;

                    const emptyResults = <div key={key} />;

                    if (!content) {
                        return emptyResults;
                    }

                    return (
                        <ResultGroup
                            key={key}
                            content={content}
                            type={type}
                            shareOptions={shareOptions}
                            onSaveEvent={onSaveEvent}
                            isAuthenticated={isAuthenticated}
                            isMobile={isMobile}
                            trackClick={trackClick}
                            trackClickInternal={trackClickInternal}
                            trackGAEvent={trackGAEvent}
                            onMouseOverCard={onMouseOverCard}
                            onMouseOutCard={onMouseOutCard}
                            featureFlags={featureFlags}
                            userCollections={userCollections}
                            onAddOrRemoveEventToCollection={
                                onAddOrRemoveEventToCollection
                            }
                            onCreateCollection={onCreateCollection}
                            shouldShowDebugOutput={shouldShowDebugOutput}
                            history={history}
                        />
                    );
                })}
            </div>
        );
    }

    return component;
};

const SearchResultSection = ({
    searchResultProps,
    isLoading,
    hasEventResults,
    isMobile,
}) => {
    let component = <SearchResults {...searchResultProps} />;

    if (isMobile) {
        component = <SearchResults {...searchResultProps} isMobile={true} />;
    }

    if (!isLoading && !hasEventResults) {
        component = null;
    }

    return component;
};

export class ResultsPanel extends React.PureComponent {
    static propTypes = {
        results: PropTypes.arrayOf(PropTypes.object),
        shareOptions: SHARE_OPTIONS_PROPTYPE.isRequired,
        onSaveEvent: PropTypes.func,
        isAuthenticated: PropTypes.bool,
        onTrackEventClickInternal: PropTypes.func,
        hasEventResults: PropTypes.bool,
        trackGAEvent: PropTypes.func,
        trackGAEventOnPageUnload: PropTypes.func,
        // TODO: Add proptype shape
        allEventsUrl: PropTypes.string,
        userCollections: PropTypes.arrayOf(PropTypes.object),
        onAddOrRemoveEventToCollection: PropTypes.func,
        onCreateCollection: PropTypes.func,
        onGoogleMapsLoaded: PropTypes.func,
    };

    componentDidMount() {
        this.props.getEventExpansions();
    }

    _handleTrackClick = (index, eventId, action) => {
        const { trackGAEvent } = this.props;

        trackGAEvent({
            action,
            category: GA_SEARCH_CATEGORY,
            label: eventId,
            dimensions: {
                eventId,
            },
            metrics: {
                searchRank: index,
            },
        });
    };

    _handleTrackClickInternal = (index, id, ticketsBy) => {
        const { onTrackEventClickInternal } = this.props;

        const trackingData = {
            clickRank: index,
            navigation: SEARCH_EVENT_NAVIGATION,
            itemId: id,
        };

        onTrackEventClickInternal(trackingData, ticketsBy);
    };

    _handleMouseOverCard = (eid) => {
        this.props.setHoverEventTarget(eid);
    };

    _handleMouseOutCard = () => {
        this.props.setHoverEventTarget(null);
    };

    render() {
        const {
            allEventsUrl,
            featureFlags,
            hasEventResults,
            history,
            isAuthenticated,
            isLoading,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            onGoogleMapsLoaded,
            onSaveEvent,
            results,
            shareOptions,
            shouldShowDebugOutput,
            trackGAEvent,
            trackGAEventOnPageUnload,
            userCollections,
        } = this.props;

        const searchResultProps = {
            featureFlags,
            history,
            isAuthenticated,
            isLoading,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            onMouseOutCard: this._handleMouseOutCard,
            onMouseOverCard: this._handleMouseOverCard,
            onSaveEvent,
            results,
            shareOptions,
            shouldShowDebugOutput,
            trackClick: this._handleTrackClick,
            trackClickInternal: this._handleTrackClickInternal,
            trackGAEvent,
            userCollections,
        };

        const searchResultsProps = {
            allEventsUrl,
            featureFlags,
            hasEventResults,
            isAuthenticated,
            isLoading,
            onSaveEvent,
            searchResultProps,
            shareOptions,
            trackGAEvent,
            trackGAEventOnPageUnload,
        };

        return (
            <div className="search-results-panel-content">
                <SearchHeaderContainer
                    onGoogleMapsLoaded={onGoogleMapsLoaded}
                    history={this.props.history}
                />
                <OnlineEventsBannerContainer
                    history={this.props.history}
                    isSearch={true}
                    containerClassName="eds-l-pad-bot-4 eds-l-pad-top-2"
                />
                <SearchResultSection {...searchResultsProps} />
            </div>
        );
    }
}

export const _mapStateToProps = (state, ownProps) => {
    const section = getSearchSection(state.location.isOnline);
    const results = getSearchContent(
        state.content.entities,
        state.pagination,
        state.currencyFormat,
        section,
    );

    return {
        results,
        history: ownProps.history,
        userCollections: getFormattedCollections(state),
        hasEventResults: hasEventResults(results),
        isAuthenticated: state.auth.isAuthenticated,
        shareOptions: state.share.options,
        isLoading: state.pagination.loading,
        featureFlags: state.app.featureFlags,
        shouldShowDebugOutput: state.admin.shouldShowDebugOutput,
        trackGAEvent: (action) =>
            trackEventFromState(state, addSearchIdToGaAction(action, state)),
        trackGAEventOnPageUnload: (action) =>
            trackEventFromStateOnPageUnload(
                state,
                addSearchIdToGaAction(
                    {
                        ...action,
                        category: GA_SEARCH_CATEGORY,
                    },
                    state,
                ),
            ),
    };
};

const _mapDispatchToProps = (dispatch) => ({
    onGoogleMapsLoaded: (...args) =>
        dispatch(setGoogleMapsLoadedAction(...args)),
    setHoverEventTarget: (...args) =>
        dispatch(setHoverEventTargetAction(...args)),
    onAddOrRemoveEventToCollection: (...args) =>
        dispatch(
            addOrRemoveEventToCollectionAction(GA_SEARCH_CATEGORY)(...args),
        ),
    onCreateCollection: (...args) =>
        dispatch(createCollectionAction(GA_SEARCH_CATEGORY)(...args)),
    onSaveEvent: (...args) => dispatch(saveEventAction(...args, true)),
    onTrackEventClickInternal: (...args) =>
        dispatch(trackEventClickInternalAction(...args)),
    getEventExpansions: (...args) =>
        dispatch(getEventExpansionsAction(...args)),
});

export default connect(_mapStateToProps, _mapDispatchToProps)(ResultsPanel);
